import { OrderEventListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import { ProfilePicture } from "../../../../components";
import { imageUrls, H5 } from "@sellar-io/components";

import { OrderEventsAdditionalInformation } from "./OrderEventsAdditionalInformation";
import { OrderEventsEditType } from "./OrderEventsEditType";
import { scheduleChangeLabel } from "../../deliveryHistory/scheduleChangeLabel";
import { timestamp } from "../../deliveryHistory/deliveryHistoryDates";

export function OrderEventListDialogItem({
  event,
}: {
  event: OrderEventListResponseType[number];
}): ReactElement {
  const isSchedulingChange = event.data.type === "sd-schedule-changed";
  let message = event.message;
  if (event.data.type === "sd-schedule-changed") {
    message = scheduleChangeLabel(event.data);
    if (!event.data.scheduledDate) {
      message += " — new date to be confirmed";
    }
  }
  const recordedAt = isSchedulingChange
    ? timestamp(event.createdAt)
    : dayjs(event.createdAt).format("DD/MM/YY HH:mm");
  const splitMessage = message.split(" by ");
  const actor = splitMessage[1];
  const hasActor = actor && actor !== "undefined";

  const type = event.data.type;

  const isOrderEditType = type === "edited";

  const isDeliveryUpsType =
    type === "delivered" && event.data.fulfillmentType === "ups";

  const logoUrl = isDeliveryUpsType
    ? imageUrls.upsLogo
    : event.BusinessUser?.Business.logoUrl;

  if (type === "promotion-applied") {
    return <PromotionAppliedEventItem event={event} logoUrl={logoUrl ?? ""} />;
  }

  return (
    <div className={"flex min-h-[50px] gap-4"}>
      <div className={"flex flex-col items-center gap-2"}>
        {!isOrderEditType && (
          <ProfilePicture
            name={event.BusinessUser?.Business.name}
            logoUrl={logoUrl}
            size={"small"}
          />
        )}
        {type !== "created" && (
          <div
            className={twMerge(
              "flex h-full w-[2px] items-center bg-zinc-300",
              isOrderEditType && "ml-[10px]",
            )}
          />
        )}
      </div>
      <div className={"flex w-full flex-col gap-8"}>
        {isOrderEditType ? (
          <OrderEventsEditType event={event} />
        ) : (
          <div className={"flex flex-col gap-4"}>
            <div className="flex flex-row justify-between">
              <p>
                {splitMessage[0]}
                {hasActor && (
                  <>
                    {" by "}
                    <strong>{actor}</strong>
                  </>
                )}{" "}
                • {recordedAt}
              </p>
              {"newOrderPrice" in event.data &&
                typeof event.data.newOrderPrice === "number" && (
                  <H5>{formatCurrency(event.data.newOrderPrice)}</H5>
                )}
            </div>
            <OrderEventsAdditionalInformation eventData={event.data} />
          </div>
        )}
      </div>
    </div>
  );
}

const PromotionAppliedEventItem = ({
  event,
  logoUrl,
}: {
  event: OrderEventListResponseType[number];
  logoUrl: string;
}) => {
  if (event.data.type !== "promotion-applied") return null;

  const description =
    event.data.spendThreshold && event.data.productName
      ? `Spend ${formatCurrency(event.data.spendThreshold)} and receive ${event.data.quantity}x ${event.data.productName} for ${event.data.promotionPrice === 0 ? "free" : event.data.promotionPrice}`
      : event.message;

  return (
    <div className="flex w-full items-center gap-4">
      <ProfilePicture
        name={event.BusinessUser?.Business.name}
        logoUrl={logoUrl}
        size="small"
      />
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 justify-between">
          <span>Promotion Applied</span>
          <span>{dayjs(event.createdAt).format("DD/MM/YY HH:mm")}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>{description}</span>
          <div className="flex gap-2">
            <span className="line-through">
              {formatCurrency(event.data.fullPrice)}
            </span>
            <span> {formatCurrency(event.data.promotionPrice)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
